import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { WebSpeechVoiceInput } from '../components/features/WebSpeechVoiceInput';
import { ShareModal } from '../components/features/ShareModal';
import { parseTranscription } from '../utils/text-parser';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { generateVCardData, downloadVCard, type VCardContact } from '../utils/vcard';
import { FiMessageSquare, FiShare, FiRotateCw } from 'react-icons/fi';
import styles from './Home.module.css';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentProfile, refreshProfile, profiles, switchProfile } = useAuth();
  const { tier } = useSubscription();
  const [_transcribedData, setTranscribedData] = useState<Partial<VCardContact>>({});
  const [error, setError] = useState<string | null>(null);
  const [lastContact, setLastContact] = useState<VCardContact | null>(null);
  const [_isListening, setIsListening] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState<string | null>(null);
  const [showInputForm, setShowInputForm] = useState(true);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  useEffect(() => {
    // Show welcome message if user just logged in
    if (location.state?.justLoggedIn && user) {
      const firstName = user.name?.split(' ')[0] || 'User';
      const isNewUser = location.state?.isNewUser;
      setWelcomeMessage(isNewUser ? `Welcome ${firstName}!` : `Welcome back, ${firstName}!`);
      // Clear welcome message after 3 seconds
      setTimeout(() => setWelcomeMessage(null), 3000);
      // Clear the state so message doesn't show on refresh
      navigate(location.pathname, { replace: true });
    }
    
    // Add the openShareModal function to the window object
    window.openShareModal = (contactName: string) => {
      // Store the contact name for use in the share modal
      sessionStorage.setItem('shareWithContactName', contactName);
      // Open the share modal
      setIsShareModalOpen(true);
    };
    
    // Clean up the function when component unmounts
    return () => {
      delete window.openShareModal;
    };
  }, [location.state, user, navigate]);

  // Ensure profile data is fresh and shortId is available
  useEffect(() => {
    const initializeProfile = async () => {
      if (!user?._id) return;
      
      // Only show loading state if we don't have a profile with shortId yet
      if (!currentProfile?.shortId) {
        setIsLoadingProfile(true);
      }
      setError(null);

      try {
        // If no current profile but we have profiles, switch to the most recent one
        if (!currentProfile && profiles && profiles.length > 0) {
          const sortedProfiles = [...profiles].sort((a, b) => 
            new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
          );
          
          if (sortedProfiles[0]?._id) {
            await switchProfile(sortedProfiles[0]._id);
            setIsLoadingProfile(false);
            return; // Exit early as switchProfile will trigger this effect again
          }
        }

        // Always refresh current profile data to ensure shortId is available
        if (currentProfile) {
          // If we already have a shortId, no need to refresh
          if (currentProfile.shortId) {
            setIsLoadingProfile(false);
            return;
          }
          
          const refreshedProfile = await refreshProfile();
          if (!refreshedProfile || !refreshedProfile.shortId) {
            console.error('Profile missing shortId after refresh:', refreshedProfile);
            setError('Profile data is not ready. Please try again in a moment.');
          } else {
            setError(null);
          }
          
          // Always set loading to false after refresh attempt
          setIsLoadingProfile(false);
        } else {
          setIsLoadingProfile(false);
        }
      } catch (error) {
        console.error('Failed to initialize profile:', error);
        setError('Failed to load profile data. Please refresh the page.');
        setIsLoadingProfile(false);
      }
    };

    initializeProfile();
  }, [user?._id, currentProfile?._id, profiles]);

  // Reset error when profile changes
  useEffect(() => {
    if (currentProfile?.shortId) {
      setError(null);
    }
  }, [currentProfile?.shortId]);

  const handleTranscription = (text: string) => {
    const parsedData = parseTranscription(text);
    
    // If we have a name and at least a phone number, create a contact
    if (parsedData.name && parsedData.phone) {
      const contact: VCardContact = {
        name: parsedData.name,
        phone: parsedData.phone || '',
        email: parsedData.email,
        notes: currentProfile?.friendlyName ? `Added with Say profile: ${currentProfile.friendlyName}` : undefined,
        categories: currentProfile?.friendlyName ? [currentProfile.friendlyName] : undefined
      };
      setTranscribedData(contact);
      setLastContact(contact);
      
      // Hide the input form when we have a valid contact
      setShowInputForm(false);
      
      // Save their contact first - this triggers immediate vCard download
      downloadVCard(contact, tier);
    } else {
      setError('Could not detect name and at least one contact method. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleVoiceStart = () => {
    setIsListening(true);
    setError(null);
  };

  const handleVoiceEnd = () => {
    setIsListening(false);
  };

  const handleSendMyCard = async () => {
    if (!user?._id || !lastContact?.phone || !user?.name || !currentProfile?.shortId) {
      setError('Missing required information. Please try again.');
      return;
    }
    
    try {
      // Construct SMS message using current profile's shortId
      const firstName = currentProfile.name?.split(' ')[0] || user?.name?.split(' ')[0] || 'User';
      const message = `Hi ${lastContact.name.split(' ')[0]}\n\nClick this link to save my contact details,\nhttps://say.cards/mysay/${currentProfile.shortId}\nKind regards\n${firstName}\n\nShare your details.\nJoin for FREE now https://say.cards`;
      
      // Encode message for SMS URI
      const encodedMessage = encodeURIComponent(message);
      
      // Open default SMS app with pre-populated message
      window.open(`sms:${lastContact.phone}?body=${encodedMessage}`, '_blank');
    } catch (error) {
      console.error('Failed to generate vCard link:', error);
      setError('Failed to generate contact sharing link. Please try again.');
    }
  };

  const handleShareMyCard = async () => {
    if (!lastContact || !currentProfile?.name || !currentProfile) return;
    
    // Create vCard for the user's contact
    const myContact: VCardContact = {
      name: currentProfile.name || 'User',
      phone: currentProfile.phone || '',
      email: user?.email
    };
    
    const vCardData = generateVCardData(myContact);
    const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' });
    const file = new File([blob], `${myContact.name}.vcf`, { type: 'text/vcard' });
    
    // Try to use the native share API first
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: `Contact details for ${myContact.name}`,
          text: `Contact details for ${myContact.name}\n\nGet your FREE SayCard now! https://say.cards`
        });
      } catch (error) {
        console.log('Share failed:', error);
        // If user cancelled, don't show error
        if ((error as any)?.name === 'AbortError') return;
        
        // Platform specific fallback
        if (/iPad|iPhone|iPod|Android/.test(navigator.userAgent)) {
          // On mobile, create SMS link as fallback
          const recipientPhone = lastContact.phone.replace(/\D/g, '');
          const smsUrl = `sms:${recipientPhone}`;
          window.location.href = smsUrl;
        } else {
          // On desktop, show a helpful message
          setError('Unable to share. Try sending your contact details another way.');
          setTimeout(() => setError(null), 3000);
        }
      }
    } else {
      // If share API not supported, provide platform-specific fallback
      if (/iPad|iPhone|iPod|Android/.test(navigator.userAgent)) {
        const recipientPhone = lastContact.phone.replace(/\D/g, '');
        const smsUrl = `sms:${recipientPhone}`;
        window.location.href = smsUrl;
      } else {
        setError('Sharing not available. Try sending your contact details another way.');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  const handleClear = () => {
    setLastContact(null);
    setTranscribedData({});
    setError(null);
    setShowInputForm(true);
  };

  return (
    <div className={styles.container}>
      {welcomeMessage && (
        <div className={styles.welcomeMessage}>
          {welcomeMessage}
        </div>
      )}

      <div className="max-w-md w-full space-y-8 bg-white p-6 rounded-xl shadow-lg">
        <div className={styles.header}>
          <h1 className={styles.title}>Swap Contact Details</h1>
          {user && (
            <button
              onClick={() => setIsShareModalOpen(true)}
              className={styles.shareButton}
              aria-label="Share selected profile"
              disabled={isLoadingProfile || !currentProfile?.shortId}
            >
              <FiShare className="w-5 h-5 mr-2" />
              Share Selected Profile
            </button>
          )}
        </div>

        {isLoadingProfile && (
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}

        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        {showInputForm ? (
          <>
            <p className={styles.instructions}>
              Speak your name and phone number or use the form to add a contact and optionally share your selected profile details back.
            </p>

            <WebSpeechVoiceInput
              onTranscription={handleTranscription}
              onStart={handleVoiceStart}
              onEnd={handleVoiceEnd}
              showForm={showInputForm}
            />
          </>
        ) : lastContact && (
          <div className={styles.actionButtons}>
            <button
              onClick={handleShareMyCard}
              className={styles.actionButton}
            >
              <FiShare className="w-5 h-5 mr-2" />
              Share with {lastContact.name.split(' ')[0]} by app
            </button>
            
            {lastContact.phone && (
              <button
                onClick={handleSendMyCard}
                className={styles.actionButton}
              >
                <FiMessageSquare className="w-5 h-5 mr-2" />
                Text my details to {lastContact.name.split(' ')[0]}
              </button>
            )}
            
            <button
              onClick={handleClear}
              className={styles.greenButton}
            >
              <FiRotateCw className="w-5 h-5 mr-2" />
              Add Another Contact
            </button>
          </div>
        )}
      </div>

      {isShareModalOpen && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
        />
      )}
    </div>
  );
};
